import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { SENERFI_LOGO_DATA_URI } from '../../assets/images/senerfiLogoDataUri';
import { PREOP_FORMAT_VERSION } from '../../pages/modules/preoperacionalDiarioConfig';
import { fetchProtectedFile } from '../../services/api';

function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtDate(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  return s.slice(0, 10);
}

function blobToDataUrl(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsDataURL(blob);
  });
}

async function toDataUrl(src) {
  const s = String(src || '').trim();
  if (!s) return '';
  if (/^data:image\//i.test(s)) return s;
  try {
    const resp = await fetch(s, { cache: 'no-store' });
    if (!resp.ok) return '';
    const blob = await resp.blob();
    if (blob && blob.type && !String(blob.type).toLowerCase().startsWith('image/')) return '';
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return '';
  }
}

// Intenta la URL subida primero (siempre disponible aunque se reabra el PDF
// más tarde); si el fetch falla (ej. CORS desde el APK nativo, o la red),
// usa el data URL local que ya se tenía en memoria al momento de firmar —
// evita que una firma real termine mostrándose como "Sin firma registrada".
async function resolveImageSrc(url, fallbackDataUrl) {
  const fromUrl = await toDataUrl(url);
  if (fromUrl) return fromUrl;
  return toDataUrl(fallbackDataUrl);
}

function sectionTableHtml(section) {
  const rows = (section.items || []).map((it) => `
    <tr>
      <td class="td label">${esc(it.label)}</td>
      <td class="td value">${esc(it.value || '-')}</td>
    </tr>
  `).join('');
  const dateRow = section.dateValue !== undefined ? `
    <tr>
      <td class="td label">${esc(section.dateFieldLabel || 'Fecha de vigencia')}</td>
      <td class="td value">${esc(fmtDate(section.dateValue) || '-')}</td>
    </tr>
  ` : '';
  return `
    <table class="section-table">
      <thead><tr><th class="th" colspan="2">${esc(section.title)}</th></tr></thead>
      <tbody>${rows}${dateRow}</tbody>
    </table>
  `;
}

async function openPreopFrame(data) {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.name = 'PRINT_PREOP';
  document.body.appendChild(frame);

  const w = frame.contentWindow;
  const doc = w.document;
  doc.open();

  const vehicle = data.vehicle || {};
  const driver = data.driver || {};
  const sections = Array.isArray(data.sections) ? data.sections : [];
  const title = `PREOP_${String(vehicle.plate || 'VEHICULO').replace(/\s+/g, '_')}_${String(data.date || '').replace(/-/g, '')}`;

  const signatureSrc = await resolveImageSrc(data.signatureUrl, data.signatureDataUrl);
  const photoSrc = await resolveImageSrc(data.photoUrl, data.photoDataUrl);
  const evidenceSrc = await resolveImageSrc(data.evidencePhotoUrl, data.evidencePhotoDataUrl);
  // Documentos opcionales del conductor (orden de trabajo, aptitud física).
  // data.documents: [{ type, label, mime, url, dataUrl }]. Si son foto se
  // imprimen; si son PDF solo se indica que están adjuntos (se abren en NOVA).
  const docsIn = Array.isArray(data.documents) ? data.documents : [];
  const attachedDocs = [];
  for (const [type, label] of [['ORDEN_TRABAJO', 'Orden de trabajo'], ['APTITUD_FISICA', 'Aptitud física']]) {
    const d = docsIn.find((x) => x && x.type === type);
    const present = !!(d && (d.url || d.dataUrl));
    const isPdf = present && (/pdf/i.test(String(d.mime || '')) || /^data:application\/pdf/i.test(String(d.dataUrl || '')));
    let imgSrc = '';
    if (present && !isPdf) {
      imgSrc = /^data:image\//i.test(String(d.dataUrl || '')) ? d.dataUrl : '';
      if (!imgSrc && d.url) {
        try { imgSrc = await blobToDataUrl(await fetchProtectedFile(d.url, { token: d.token })); } catch (_) { imgSrc = ''; }
      }
    }
    attachedDocs.push({ label, present, isPdf, imgSrc });
  }
  const anyAttachedDoc = attachedDocs.some((d) => d.present);

  // El logo va incrustado como data URI (SENERFI_LOGO_DATA_URI): nunca
  // depende de un fetch en tiempo de ejecución, así que siempre sale a
  // color, tanto en la web como dentro del APK nativo.
  const headerLogo = `<img src="${SENERFI_LOGO_DATA_URI}" style="height:40px; object-fit:contain" />`;

  const sectionsHtml = sections.map((sec) => sectionTableHtml(sec)).join('');

  const authSignatures = Array.isArray(data.authSignatures) ? data.authSignatures : [];
  const authHtmlParts = [];
  for (const a of authSignatures) {
    const sigSrc = await resolveImageSrc(a.signatureUrl, a.signatureDataUrl);
    authHtmlParts.push(`
      <div class="auth-box">
        <div class="small"><b>${esc(a.role)}</b></div>
        <div style="margin-top:4px">Nombre: ${esc(a.name)}</div>
        ${a.notes ? `<div style="margin-top:4px" class="small">Observación: ${esc(a.notes)}</div>` : ''}
        <div style="margin-top:6px; min-height:50px; border-top:1px solid #9ca3af; padding-top:4px; text-align:center">
          ${sigSrc ? `<img src="${esc(sigSrc)}" style="max-height:44px; max-width:100%; object-fit:contain" />` : '<span class="small">Sin firma</span>'}
        </div>
      </div>
    `);
  }
  const authHtml = authHtmlParts.join('');

  doc.write(`<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Arial, Helvetica, sans-serif; background: #fff; color: #111827; }
      .sheet { width: 900px; padding: 18px; }
      .header-table { border: 1px solid #111827; border-collapse: collapse; }
      .header-table td { border: 1px solid #111827; padding: 6px 10px; font-size: 12px; vertical-align: middle; }
      .header-table .logo-cell { width: 130px; text-align: center; }
      .header-table .company-cell { font-weight: 700; font-size: 13px; }
      .header-table .dept-cell { font-weight: 700; font-size: 12px; text-align: center; }
      .header-table .title-cell { font-weight: 700; font-size: 13px; text-align: center; text-transform: uppercase; }
      .header-table .meta-label { font-weight: 700; width: 70px; background: #f3f4f6; }
      .header-table .meta-value { width: 140px; }
      .muted { color: #4b5563; font-size: 11px; }
      .box { border: 1px solid #111827; }
      .p10 { padding: 10px; }
      table { width: 100%; border-collapse: collapse; }
      .th { border: 1px solid #111827; background: #e5e7eb; padding: 6px 8px; font-size: 12px; text-align: center; font-weight: 700; }
      .td { border: 1px solid #111827; padding: 5px 8px; font-size: 11px; vertical-align: top; }
      .label { width: 62%; }
      .value { width: 38%; font-weight: 600; }
      .section-table { margin-top: 10px; }
      .kv { display: grid; grid-template-columns: 190px 1fr; gap: 4px 10px; font-size: 11px; }
      .kv b { font-weight: 700; }
      .small { font-size: 10px; color: #374151; }
      .foot { margin-top: 10px; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .sig-box { min-height: 100px; border: 1px solid #111827; padding: 8px; text-align: center; }
      .sig-box img { max-height: 80px; max-width: 100%; object-fit: contain; }
      .auth-grid { margin-top: 10px; display: grid; grid-template-columns: 1fr; gap: 10px; }
      .auth-box { border: 1px solid #111827; padding: 8px; min-height: 60px; }
      .consecutivo { margin-top: 10px; text-align: right; font-size: 11px; font-weight: 700; }
    </style>
  </head>
  <body>
    <div class="sheet">
      <table class="header-table">
        <tr>
          <td class="logo-cell" rowspan="3">${headerLogo}<div class="muted" style="margin-top:4px">Soluciones que Generan Desarrollo</div></td>
          <td class="company-cell">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES S.A.S</td>
          <td class="meta-label">Código</td>
          <td class="meta-value">${esc(data.formatCode || '')}</td>
        </tr>
        <tr>
          <td class="dept-cell">TRANSPORTE Y MANTENIMIENTO</td>
          <td class="meta-label">Versión</td>
          <td class="meta-value">${esc(PREOP_FORMAT_VERSION)}</td>
        </tr>
        <tr>
          <td class="title-cell">${esc(data.formatLabel || 'Preoperacional')}</td>
          <td class="meta-label">Fecha</td>
          <td class="meta-value">${esc(data.date || '')}</td>
        </tr>
      </table>

      <table class="section-table">
        <thead><tr><th class="th" colspan="2">INFORMACIÓN DEL VEHÍCULO</th></tr></thead>
        <tbody>
          <tr><td class="td label">Placa</td><td class="td value">${esc(vehicle.plate || '')}</td></tr>
          <tr><td class="td label">Marca / Modelo</td><td class="td value">${esc([vehicle.brand, vehicle.model].filter(Boolean).join(' / '))}</td></tr>
          <tr><td class="td label">Carpeta documental</td><td class="td value">${vehicle.hasDocuments ? 'SI' : 'NO'}</td></tr>
          <tr><td class="td label">Fecha vigencia SOAT</td><td class="td value">${esc(fmtDate(vehicle.soatExpiry)) || '-'}</td></tr>
          <tr><td class="td label">Fecha vigencia Rev. Tecnomecánica</td><td class="td value">${esc(fmtDate(vehicle.tecnomecanicaExpiry)) || '-'}</td></tr>
          <tr><td class="td label">Fecha vigencia Póliza</td><td class="td value">${esc(fmtDate(vehicle.policyExpiry)) || '-'}</td></tr>
        </tbody>
      </table>

      <table class="section-table">
        <thead><tr><th class="th" colspan="2">INFORMACIÓN DEL CONDUCTOR</th></tr></thead>
        <tbody>
          <tr><td class="td label">Conductor</td><td class="td value">${esc(driver.nombre || '')}</td></tr>
          <tr><td class="td label">Cédula</td><td class="td value">${esc(driver.cedula || '')}</td></tr>
          <tr><td class="td label">Licencia de conducción</td><td class="td value">${esc(driver.licenseNumber || '') || '-'}</td></tr>
          <tr><td class="td label">Fecha vencimiento licencia</td><td class="td value">${esc(fmtDate(driver.licenseExpiry)) || '-'}</td></tr>
          <tr><td class="td label">Categoría de licencia</td><td class="td value">${esc(driver.licenseCategory || '') || '-'}</td></tr>
        </tbody>
      </table>

      <table class="section-table">
        <thead><tr><th class="th" colspan="2">CONTROL DE ${esc((data.mileageLabel || 'KILOMETRAJE').toUpperCase())}</th></tr></thead>
        <tbody>
          <tr><td class="td label">${esc(data.mileageLabel || 'Kilometraje')} inicial</td><td class="td value">${esc(data.mileage ?? '') || '-'}</td></tr>
          <tr><td class="td label">Proyecto / Responsable</td><td class="td value">${esc(data.projectResponsible || '') || '-'}</td></tr>
        </tbody>
      </table>

      ${sectionsHtml}

      <table class="section-table">
        <thead><tr><th class="th" colspan="2">OBSERVACIONES</th></tr></thead>
        <tbody>
          <tr><td class="td label">¿Hay hallazgos o seguimiento?</td><td class="td value">${esc(data.hasFindings || 'No')}</td></tr>
          <tr><td class="td label">Observaciones generales</td><td class="td value" style="white-space:pre-wrap; word-break:break-word; font-weight:400">${esc(data.observations || '') || '-'}</td></tr>
        </tbody>
      </table>

      ${evidenceSrc ? `
      <table class="section-table">
        <thead><tr><th class="th">EVIDENCIA FOTOGRÁFICA DE HALLAZGOS</th></tr></thead>
        <tbody><tr><td class="td" style="text-align:center; padding:10px">
          <img src="${esc(evidenceSrc)}" style="max-height:220px; max-width:100%; object-fit:contain" />
        </td></tr></tbody>
      </table>
      ` : ''}

      ${anyAttachedDoc ? `
      <table class="section-table">
        <thead><tr><th class="th" colspan="2">DOCUMENTOS ADJUNTOS</th></tr></thead>
        <tbody>
          ${attachedDocs.map((d) => `<tr><td class="td label">${esc(d.label)}</td><td class="td value">${d.present ? (d.isPdf ? 'Adjunto (PDF, se consulta en NOVA)' : 'Adjunto (foto)') : 'No adjunto'}</td></tr>`).join('')}
          ${attachedDocs.filter((d) => d.imgSrc).map((d) => `<tr><td class="td" colspan="2" style="text-align:center; padding:10px">
            <div class="small" style="margin-bottom:6px">${esc(d.label)}</div>
            <img src="${esc(d.imgSrc)}" style="max-height:320px; max-width:100%; object-fit:contain" />
          </td></tr>`).join('')}
        </tbody>
      </table>
      ` : ''}

      ${data.approval && (data.approval.status === 'aprobado' || data.approval.status === 'bloqueado') ? `
      <table class="section-table">
        <thead><tr><th class="th" colspan="2">APROBACIÓN DEL PREOPERACIONAL</th></tr></thead>
        <tbody>
          <tr><td class="td label">Decisión</td><td class="td value">${data.approval.status === 'bloqueado' ? 'SALIDA BLOQUEADA' : 'APROBADO'}</td></tr>
          <tr><td class="td label">Revisó</td><td class="td value">${esc(data.approval.by || '-')} · ${esc(data.approval.at || '')}</td></tr>
          ${data.approval.notes ? `<tr><td class="td label">Observación</td><td class="td value" style="white-space:pre-wrap; font-weight:400">${esc(data.approval.notes)}</td></tr>` : ''}
        </tbody>
      </table>
      ` : ''}

      <table class="section-table">
        <thead><tr><th class="th" colspan="2">FIRMA Y FOTO DEL RESPONSABLE</th></tr></thead>
      </table>
      <div class="foot">
        <div class="sig-box">
          ${signatureSrc ? `<img src="${esc(signatureSrc)}" />` : '<div class="small">Sin firma registrada</div>'}
          <div class="small" style="margin-top:6px; border-top:1px solid #9ca3af; padding-top:4px">Firma del Conductor</div>
        </div>
        <div class="sig-box">
          ${photoSrc ? `<img src="${esc(photoSrc)}" />` : '<div class="small">Sin foto registrada</div>'}
          <div class="small" style="margin-top:6px; border-top:1px solid #9ca3af; padding-top:4px">Foto del Conductor</div>
        </div>
      </div>

      <table class="section-table">
        <thead><tr><th class="th" colspan="2">AUTORIZACIÓN VEHÍCULO</th></tr></thead>
      </table>
      <div class="auth-grid">${authHtml}</div>
      <div class="consecutivo">Consecutivo: ${esc(data.consecutivo || '')}</div>
    </div>
    <script>
      window.onload = () => { window.__PREOP_READY__ = true; };
    </script>
  </body>
  </html>`);
  doc.close();

  return { frame, w, title };
}

export async function generatePreoperacionalPdfBlob(data) {
  const { frame, w, title } = await openPreopFrame(data);
  try {
    for (let i = 0; i < 60; i++) {
      if (w.__PREOP_READY__) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    const imgs = Array.from(w.document.images || []);
    await Promise.all(imgs.map((img) => (
      img.complete ? Promise.resolve() : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
    )));
    await new Promise((r) => setTimeout(r, 60));

    const el = w.document.querySelector('.sheet') || w.document.body;
    const ww = Math.max(1000, Number(el?.scrollWidth || 0) || 0);
    const wh = Math.max(1400, Number(el?.scrollHeight || 0) || 0);
    const renderScale = 2;
    const canvas = await html2canvas(el, {
      scale: renderScale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
      windowWidth: ww,
      windowHeight: wh,
    });

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 6;
    const pxToMm = 0.26458333333333334;
    const mmW = (canvas.width / renderScale) * pxToMm;
    const mmH = (canvas.height / renderScale) * pxToMm;
    const maxW = pageW - (margin * 2);
    const pageInnerH = pageH - (margin * 2);
    const fitScale = Math.min(1, maxW / mmW);
    const drawW = mmW * fitScale;
    const drawH = mmH * fitScale;

    let offsetY = 0;
    pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    while (offsetY + pageInnerH < drawH) {
      offsetY += pageInnerH;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    }
    const blob = pdf.output('blob');
    return { blob, title };
  } finally {
    try { frame.remove(); } catch (_) {}
  }
}
